<template>
  <div class="info-center" :class="{ 'is-editing': editing, 'is-reading': viewingDoc }">
    <section class="info-hero" aria-labelledby="info-center-title">
      <div class="info-hero__photo" aria-hidden="true">
        <img :src="heroSrc" alt="" />
      </div>
      <div class="info-hero__inner">
        <p class="info-hero__kicker">{{ stage.kicker }}</p>
        <h1 id="info-center-title">{{ stage.title }}</h1>
        <p v-if="stage.desc" class="info-hero__lead">{{ stage.desc }}</p>
      </div>
    </section>

    <div v-if="isAdminLoggedIn" class="info-center__adminbar">
      <button type="button" class="portal-btn portal-btn--secondary" @click="toggleEdit">
        {{ editing ? '편집 끝내기' : '소개 편집' }}
      </button>
      <router-link to="/info/library" class="info-center__library">폴더·HTML 관리</router-link>
      <p v-if="note" class="info-center__note" role="status">{{ note }}</p>
    </div>

    <form v-if="isAdminLoggedIn && editing" class="info-center__stage-edit" @submit.prevent="saveStage">
      <p class="info-center__edit-label">소개 영역</p>
      <label>작은 문구 <input v-model="stageDraft.kicker" class="portal-input" maxlength="40" /></label>
      <label>제목 <input v-model="stageDraft.title" class="portal-input" maxlength="80" /></label>
      <label>설명 <textarea v-model="stageDraft.desc" class="portal-input" rows="2" maxlength="300" /></label>
      <div class="info-center__stage-photo">
        <img :src="stageDraft.heroImage || heroFallback" alt="" />
        <label class="info-center__file">
          배경 이미지
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" @change="onStageImage" />
        </label>
        <button type="button" class="portal-btn portal-btn--secondary" :disabled="!stageDraft.heroImage" @click="stageDraft.heroImage = ''">
          이미지 제거
        </button>
      </div>
      <div class="info-center__edit-actions">
        <button type="button" class="portal-btn portal-btn--secondary" @click="revertStage">저장 전으로</button>
        <button type="button" class="portal-btn portal-btn--secondary" :disabled="saving || !stage.hasPrev" @click="onRestoreStage">
          직전 저장본
        </button>
        <button type="submit" class="portal-btn portal-btn--primary" :disabled="saving">소개 저장</button>
      </div>
    </form>

    <div class="info-center__body">
      <aside class="info-center__side">
        <p class="info-center__side-title">폴더</p>
        <nav class="info-center__side-nav" aria-label="정보마당 폴더">
          <div class="info-center__side-group">
            <div class="info-center__side-row" :class="{ 'is-on': folderId === 0 }">
              <button type="button" class="info-center__side-item" :aria-current="folderId === 0 ? 'true' : undefined" @click="selectFolder(0)">
                <AppIcon icon="mdi-home-outline" :size="18" />
                <span>전체</span>
                <em>{{ docs.length }}</em>
              </button>
            </div>
          </div>
          <div v-for="row in visibleFolders" :key="row.idx" class="info-center__side-group">
            <div class="info-center__side-row" :class="{ 'is-on': folderId === row.idx }" :style="{ paddingLeft: `${row.depth * 14}px` }">
              <button
                v-if="row.hasChildren"
                type="button"
                class="info-center__chev"
                :aria-expanded="isOpen(row.idx)"
                :aria-label="`${row.name} ${isOpen(row.idx) ? '접기' : '펼치기'}`"
                @click="toggleFolder(row.idx)"
              >
                <AppIcon :icon="isOpen(row.idx) ? 'mdi-chevron-down' : 'mdi-chevron-right'" :size="16" />
              </button>
              <button
                type="button"
                class="info-center__side-item"
                :aria-current="folderId === row.idx ? 'true' : undefined"
                @click="selectFolder(row.idx)"
              >
                <AppIcon icon="mdi-folder-outline" :size="18" />
                <span>{{ row.name }}</span>
                <i v-if="row.status === 'HIDDEN'" class="info-card__tag is-hidden">숨김</i>
                <em>{{ countOf(row.idx) }}</em>
              </button>
            </div>
          </div>
        </nav>
        <div class="info-side__help">
          <span class="info-side__help-icon" aria-hidden="true">
            <AppIcon icon="mdi-headset" :size="22" />
          </span>
          <p class="info-side__help-title">궁금한 사항이 있으신가요?</p>
          <p class="info-side__help-copy">전문 상담사가 친절하게<br />안내해드립니다.</p>
          <router-link to="/consult-board/write" class="info-side__help-btn">상담하기 <span aria-hidden="true">→</span></router-link>
        </div>
      </aside>

      <div ref="mainEl" class="info-center__main">
        <template v-if="viewingDoc">
          <p v-if="docLoading && !activeDoc" class="info-center__status">불러오는 중…</p>
          <p v-else-if="docError" class="info-center__status">{{ docError }}</p>
          <div v-else-if="activeDoc" class="info-center__reader" :class="{ 'is-full': htmlFull }">
            <div class="info-center__reader-tools">
              <p v-if="linkNote" class="info-center__link-note">{{ linkNote }}</p>
              <button type="button" class="info-center__full" @click="toggleHtmlFull">전체화면</button>
            </div>
            <button
              v-if="htmlFull"
              type="button"
              class="info-html-exit"
              aria-label="전체화면 닫기"
              @click="exitHtmlFull"
            >
              <AppIcon icon="mdi-close" :size="20" />
            </button>
            <iframe
              ref="frameEl"
              class="info-center__html"
              :srcdoc="htmlBody"
              :sandbox="INFO_FRAME_SANDBOX"
              allowfullscreen
              :allow="INFO_FRAME_ALLOW"
              :title="activeDoc.title"
              referrerpolicy="no-referrer"
            />
          </div>
        </template>

        <header v-if="!viewingDoc" class="info-center__list-head">
          <div>
            <h2>{{ heading }}</h2>
            <p>총 {{ filteredDocs.length }}개의 게시물이 있습니다.</p>
          </div>
          <div class="info-center__tools">
            <label class="info-center__sort">
              <span class="info-center__sr">정렬</span>
              <select v-model="sort" @change="commitFilters">
                <option value="latest">최신순</option>
                <option value="oldest">오래된순</option>
                <option value="title">제목순</option>
              </select>
            </label>
          </div>
        </header>

        <p v-if="!viewingDoc && loading" class="info-center__status">불러오는 중…</p>
        <p v-else-if="!viewingDoc && errorMessage" class="info-center__status">{{ errorMessage }}</p>
        <p v-else-if="!viewingDoc && !filteredDocs.length" class="info-center__status">등록된 글이 없습니다.</p>

        <div v-else-if="!viewingDoc" class="info-center__cards">
          <article v-for="item in pageDocs" :key="item.idx" class="info-card">
            <router-link :to="`/info/${item.id}`" class="info-card__photo">
              <img :src="cardImage(item)" alt="" />
            </router-link>
            <div class="info-card__body">
              <p class="info-card__date">
                <AppIcon icon="mdi-calendar-blank-outline" :size="15" />
                <time :datetime="item.createdAt">{{ formatDate(item.createdAt) }}</time>
              </p>
              <h3 class="info-card__title">
                <router-link :to="`/info/${item.id}`">{{ item.title }}</router-link>
              </h3>
              <p v-if="item.summary" class="info-card__summary">{{ item.summary }}</p>
              <div class="info-card__tags">
                <span class="info-card__tag" :style="{ background: tagColor(item.folderIdx) }">{{ item.folderName || '정보마당' }}</span>
                <span v-if="item.status === 'HIDDEN'" class="info-card__tag is-hidden">숨김</span>
              </div>
            </div>
          </article>
        </div>

        <nav v-if="!viewingDoc && pageCount > 1" class="info-center__pages" aria-label="글 페이지">
          <button type="button" class="info-center__pages-step" :disabled="page === 1" aria-label="이전 페이지" @click="goPage(page - 1)">‹</button>
          <template v-for="item in pageItems" :key="item.key">
            <span v-if="item.dots" class="info-center__pages-dots">…</span>
            <button
              v-else
              type="button"
              class="info-center__pages-num"
              :class="{ 'is-on': item.n === page }"
              :aria-current="item.n === page ? 'page' : undefined"
              @click="goPage(item.n!)"
            >{{ item.n }}</button>
          </template>
          <button type="button" class="info-center__pages-step" :disabled="page === pageCount" aria-label="다음 페이지" @click="goPage(page + 1)">›</button>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useInfoHtmlFrame } from '@/composables/useInfoHtmlFrame'
import { INFO_FRAME_ALLOW, INFO_FRAME_SANDBOX, renderInfoHtmlDocument } from '@/utils/infoHtmlFrame'
import {
  fetchAdminInfo,
  fetchPublicInfo,
  fetchPublicInfoDoc,
  type InfoDoc,
  type InfoFolder,
} from '@/api/infoPlaza'
import {
  restoreInfoStage,
  saveInfoStage,
  uploadInfoImage,
  type InfoStage,
} from '@/api/infoCenter'

const PAGE_SIZE = 6
const heroFallback = '/images/info/hero-desk.jpg'
const thumbPool = [
  '/images/info/thumb-a.jpg',
  '/images/info/thumb-b.jpg',
  '/images/info/thumb-c.jpg',
  '/images/info/thumb-d.jpg',
]
const tagPalette = ['#d4533c', '#3b6fd8', '#1f8a4c', '#6a568f', '#243044', '#c47a2c']

const { isAdminLoggedIn, initAdminAuth } = useAdminAuth()
const route = useRoute()
const router = useRouter()

const folders = ref<InfoFolder[]>([])
const docs = ref<InfoDoc[]>([])
const openIds = ref<Set<number>>(new Set())
const treeReady = ref(false)
const stage = ref<InfoStage>({ kicker: 'INFORMATION CENTER', title: '정보마당', desc: '', heroImage: '' })
const stageDraft = reactive({ kicker: '', title: '', desc: '', heroImage: '' })
const stageSaved = ref('')
const folderId = ref(0)
const sort = ref('latest')
const loading = ref(true)
const errorMessage = ref('')
const note = ref('')
const saving = ref(false)
const editing = ref(false)
const page = ref(1)
const mainEl = ref<HTMLElement | null>(null)
const frameEl = ref<HTMLIFrameElement | null>(null)
const activeDoc = ref<InfoDoc | null>(null)
const docLoading = ref(false)
const docError = ref('')

const viewingDoc = computed(() => route.name === 'info-detail')
const linkNote = ref('')
const focusId = ref('')
const currentDocId = computed(() => activeDoc.value?.id || '')
const htmlBody = computed(() =>
  renderInfoHtmlDocument(activeDoc.value?.content || '', {
    fragmentCss:
      ":root{color-scheme:light}body{margin:0;padding:4px 0;font-family:'Noto Sans KR','Apple SD Gothic Neo',system-ui,sans-serif;line-height:1.85;color:#1b1f27;word-break:keep-all}img{max-width:100%;height:auto}table{border-collapse:collapse}",
  }),
)

const { htmlFull, toggleHtmlFull, exitHtmlFull } = useInfoHtmlFrame({
  frameEl,
  docs,
  currentId: currentDocId,
  focusId,
  onOpenDoc: (id, hash) => {
    const query = buildQuery()
    if (hash) query.at = hash
    void router.push({ name: 'info-detail', params: { code: id }, query })
  },
  onMissing: (message) => {
    linkNote.value = message
  },
})

const heroSrc = computed(() => stage.value.heroImage || heroFallback)

const childrenOf = computed(() => {
  const map = new Map<number, InfoFolder[]>()
  folders.value.forEach((folder) => {
    const key = folder.parentIdx ?? 0
    const list = map.get(key) || []
    list.push(folder)
    map.set(key, list)
  })
  map.forEach((list) => list.sort((a, b) => a.sort - b.sort || a.idx - b.idx))
  return map
})

const visibleFolders = computed(() => {
  const rows: Array<InfoFolder & { depth: number; hasChildren: boolean }> = []
  const walk = (parentIdx: number, depth: number) => {
    ;(childrenOf.value.get(parentIdx) || []).forEach((folder) => {
      const hasChildren = (childrenOf.value.get(folder.idx) || []).length > 0
      rows.push({ ...folder, depth, hasChildren })
      if (hasChildren && openIds.value.has(folder.idx)) walk(folder.idx, depth + 1)
    })
  }
  walk(0, 0)
  return rows
})

const filteredDocs = computed(() => {
  const allowed = folderId.value ? new Set(folderScope(folderId.value)) : null
  const rows = docs.value.filter((doc) => !allowed || allowed.has(doc.folderIdx))
  rows.sort((a, b) => {
    if (sort.value === 'title') return a.title.localeCompare(b.title, 'ko') || b.idx - a.idx
    if (sort.value === 'oldest') return a.createdAt.localeCompare(b.createdAt) || a.idx - b.idx
    return b.createdAt.localeCompare(a.createdAt) || b.idx - a.idx
  })
  return rows
})

const heading = computed(() => {
  if (!folderId.value) return '전체'
  return folders.value.find((folder) => folder.idx === folderId.value)?.name || '전체'
})

const pageCount = computed(() => Math.max(1, Math.ceil(filteredDocs.value.length / PAGE_SIZE)))
const pageDocs = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return filteredDocs.value.slice(start, start + PAGE_SIZE)
})
const pageItems = computed(() => {
  const total = pageCount.value
  const current = page.value
  const items: Array<{ key: string; n?: number; dots?: boolean }> = []
  const push = (n: number) => items.push({ key: `p${n}`, n })
  if (total <= 7) {
    for (let i = 1; i <= total; i += 1) push(i)
    return items
  }
  const windowStart = Math.max(2, Math.min(current - 1, total - 4))
  const windowEnd = Math.min(total - 1, windowStart + 3)
  push(1)
  if (windowStart > 2) items.push({ key: 'd1', dots: true })
  for (let i = windowStart; i <= windowEnd; i += 1) push(i)
  if (windowEnd < total - 1) items.push({ key: 'd2', dots: true })
  push(total)
  return items
})

function folderScope(idx: number) {
  const ids = [idx]
  const walk = (parent: number) => {
    ;(childrenOf.value.get(parent) || []).forEach((folder) => {
      ids.push(folder.idx)
      walk(folder.idx)
    })
  }
  walk(idx)
  return ids
}

function countOf(idx: number) {
  const ids = new Set(folderScope(idx))
  return docs.value.filter((doc) => ids.has(doc.folderIdx)).length
}

function isOpen(idx: number) {
  return openIds.value.has(idx)
}

function toggleFolder(idx: number) {
  const next = new Set(openIds.value)
  if (next.has(idx)) next.delete(idx)
  else next.add(idx)
  openIds.value = next
}

function formatDate(value: string) {
  return String(value || '').slice(0, 10).replace(/-/g, '.')
}

function cardImage(item: InfoDoc) {
  if (item.cover) return item.cover
  return thumbPool[Math.abs(item.idx || 0) % thumbPool.length]
}

function tagColor(folderIdx: number) {
  return tagPalette[Math.abs(folderIdx || 0) % tagPalette.length]
}

function rememberStage() {
  stageSaved.value = JSON.stringify({ ...stageDraft })
}

function applyStage(next?: InfoStage) {
  if (!next) return
  stage.value = next
  Object.assign(stageDraft, {
    kicker: next.kicker,
    title: next.title,
    desc: next.desc,
    heroImage: next.heroImage,
  })
  rememberStage()
}

function applyRouteQuery() {
  const raw = Number(route.query.folder)
  folderId.value = Number.isFinite(raw) && raw > 0 ? raw : 0
  const nextSort = String(route.query.sort || 'latest')
  sort.value = nextSort === 'oldest' || nextSort === 'title' ? nextSort : 'latest'
}

function buildQuery() {
  const query: Record<string, string> = {}
  if (folderId.value) query.folder = String(folderId.value)
  if (sort.value !== 'latest') query.sort = sort.value
  return query
}

function sameQuery(next: Record<string, string>) {
  const current = {
    folder: String(route.query.folder || ''),
    sort: String(route.query.sort || ''),
  }
  return current.folder === (next.folder || '') && current.sort === (next.sort || '')
}

function commitFilters() {
  page.value = 1
  const next = buildQuery()
  if (sameQuery(next)) {
    applyRouteQuery()
    return
  }
  void router.replace({ query: next })
}

function selectFolder(idx: number) {
  folderId.value = idx
  page.value = 1
  if (route.name === 'info-detail') {
    void router.push({ name: 'info-center', query: buildQuery() })
    return
  }
  commitFilters()
}

function revealFolder(idx: number) {
  const next = new Set(openIds.value)
  let current = folders.value.find((folder) => folder.idx === idx)
  while (current?.parentIdx) {
    next.add(current.parentIdx)
    const parentIdx = current.parentIdx
    current = folders.value.find((folder) => folder.idx === parentIdx)
  }
  openIds.value = next
}

async function focusReader() {
  await nextTick()
  mainEl.value?.scrollIntoView({ block: 'start', behavior: 'auto' })
}

async function loadDoc() {
  const code = String(route.params.code || '')
  if (route.name !== 'info-detail' || !code) return
  docLoading.value = true
  docError.value = ''
  activeDoc.value = null
  try {
    const doc = await fetchPublicInfoDoc(code)
    if (route.name !== 'info-detail' || String(route.params.code || '') !== code) return
    activeDoc.value = doc
    if (doc.folderIdx) {
      folderId.value = doc.folderIdx
      revealFolder(doc.folderIdx)
    }
    void focusReader()
  } catch (error) {
    if (route.name !== 'info-detail' || String(route.params.code || '') !== code) return
    if (!isAdminLoggedIn.value) {
      docError.value = error instanceof Error ? error.message : '콘텐츠를 찾을 수 없습니다.'
    } else {
      try {
        const data = await fetchAdminInfo()
        const doc = data.docs.find((item) => item.id === code) ?? null
        activeDoc.value = doc
        if (doc?.folderIdx) {
          folderId.value = doc.folderIdx
          revealFolder(doc.folderIdx)
        }
        if (!doc) docError.value = '콘텐츠를 찾을 수 없습니다.'
        else void focusReader()
      } catch (adminError) {
        docError.value = adminError instanceof Error ? adminError.message : '콘텐츠를 찾을 수 없습니다.'
      }
    }
  } finally {
    if (String(route.params.code || '') === code) docLoading.value = false
  }
}

function goPage(n: number) {
  page.value = Math.min(pageCount.value, Math.max(1, n))
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const data = isAdminLoggedIn.value ? await fetchAdminInfo() : await fetchPublicInfo()
    folders.value = data.folders
    docs.value = data.docs
    applyStage(data.stage)
    if (activeDoc.value?.folderIdx) revealFolder(activeDoc.value.folderIdx)
    if (!treeReady.value) {
      openIds.value = new Set(
        folders.value
          .filter((folder) => folders.value.some((child) => child.parentIdx === folder.idx))
          .map((folder) => folder.idx),
      )
      treeReady.value = true
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '정보마당을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function toggleEdit() {
  if (!isAdminLoggedIn.value) return
  editing.value = !editing.value
  if (editing.value) rememberStage()
}

function revertStage() {
  if (!stageSaved.value) return
  Object.assign(stageDraft, JSON.parse(stageSaved.value))
}

async function readImage(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return ''
  return uploadInfoImage(file)
}

async function onStageImage(event: Event) {
  try {
    const url = await readImage(event)
    if (url) stageDraft.heroImage = url
  } catch (error) {
    note.value = error instanceof Error ? error.message : '이미지를 올리지 못했습니다.'
  }
}

async function saveStage() {
  saving.value = true
  note.value = ''
  try {
    applyStage(await saveInfoStage({ ...stageDraft }))
    note.value = '소개 영역을 저장했습니다.'
  } catch (error) {
    note.value = error instanceof Error ? error.message : '소개 영역을 저장하지 못했습니다.'
  } finally {
    saving.value = false
  }
}

async function onRestoreStage() {
  saving.value = true
  note.value = ''
  try {
    applyStage(await restoreInfoStage())
    note.value = '직전 소개 영역으로 되돌렸습니다.'
  } catch (error) {
    note.value = error instanceof Error ? error.message : '되돌리지 못했습니다.'
  } finally {
    saving.value = false
  }
}

watch(
  () => String(route.query.at || ''),
  (id) => {
    focusId.value = id
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    linkNote.value = ''
    if (route.name === 'info-detail') {
      void loadDoc()
      return
    }
    void exitHtmlFull()
    if (route.name !== 'info-center') return
    activeDoc.value = null
    docError.value = ''
    applyRouteQuery()
    page.value = 1
  },
)

watch(isAdminLoggedIn, (on) => {
  if (!on) editing.value = false
  if (!loading.value) void load()
})

watch(pageCount, (total) => {
  if (page.value > total) page.value = total
})

onMounted(async () => {
  applyRouteQuery()
  await initAdminAuth()
  await Promise.all([load(), loadDoc()])
})
</script>

<style lang="scss" src="./InfoCenterPage.scss"></style>
